import React, { useState, useEffect, useRef, useCallback } from "react";
import { exportUsersCsv, fetchUsersAPI, resetUserPasswordAPI, updateUserAPI, getUserData } from "../../utils/api";
import { showConfirmation, showError, showInfo, showSuccess } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, Modal, Pagination, SearchField, StatCard } from "../../components/ui";
import GrantSubscriptionModal from "../../components/GrantSubscriptionModal";
import "./Users.css";

const DEFAULT_PAGINATION = {
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 1,
  has_more: false,
};

// Filter chips, applied by the server across every page. total: the key in response.totals
const ROLE_FILTERS = [
  { key: "all", label: "All", params: {}, total: "all" },
  { key: "premium", label: "Premium", params: { subscribed: 1 }, total: "subscribed" },
  { key: "free", label: "Free", params: { subscribed: 0, role: "user" } },
  { key: "admin", label: "Admins", params: { role: "admin" }, total: "admins" },
  { key: "blocked", label: "Blocked", params: { status: "blocked" }, total: "blocked" },
];

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("") || "?";

// effective_status: the stored status combined with the expiry date (a run-out plan is "expired")
const subStatus = (sub) => sub.effective_status || sub.status || "active";
const activeSubsOf = (user) => (user.subscriptions || []).filter((s) => subStatus(s) === "active");
// The server works out is_subscribed from the subscriptions' dates
const isPremiumUser = (user) => Boolean(Number(user.is_subscribed)) || activeSubsOf(user).length > 0;
const isBlocked = (user) => user.status === "blocked";

const statusTone = (status) =>
  ({ active: "badge-success", expired: "badge-danger", cancelled: "badge-danger", failed: "badge-danger", pending: "badge-warning" })[
    status
  ] || "badge-neutral";
const STATUS_LABELS = { pending: "payment pending" };

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [totals, setTotals] = useState(null);
  const [grantUser, setGrantUser] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [exporting, setExporting] = useState(false);
  const me = getUserData();

  const [detailsUser, setDetailsUser] = useState(null);
  const [subscriptionsUser, setSubscriptionsUser] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);

  const firstLoad = useRef(true);

  const fetchUsers = useCallback(async (page = 1, search = "", perPageCount = 10, filterKey = "all") => {
    setLoading(true);
    setError(null);
    try {
      const chip = ROLE_FILTERS.find((f) => f.key === filterKey) || ROLE_FILTERS[0];
      const response = await fetchUsersAPI({ search, page, per_page: perPageCount, ...chip.params });

      if (response?.success) {
        setUsers(response.data || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, per_page: perPageCount });
        setTotals(response.totals || null);
      } else if (response) {
        setError(response.message || "Failed to load users.");
        setUsers([]);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Couldn't reach the server to load users.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Single source of loading: runs on mount and (debounced) whenever search / page size change
  useEffect(() => {
    const delay = firstLoad.current ? 0 : 450;
    firstLoad.current = false;
    const timer = setTimeout(() => fetchUsers(1, searchTerm.trim(), perPage, roleFilter), delay);
    return () => clearTimeout(timer);
  }, [searchTerm, perPage, roleFilter, fetchUsers]);

  const currentPage = pagination.current_page || 1;
  const lastPage = pagination.last_page || 1;

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    fetchUsers(page, searchTerm.trim(), perPage, roleFilter);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const refreshUsers = () => fetchUsers(currentPage, searchTerm.trim(), perPage, roleFilter);

  // The server already applied the chip
  const filteredUsers = users;

  // Replace one user in the list and in an open dialog after a change
  const replaceUser = (updated) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    setDetailsUser((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const changeUser = async (user, changes, confirm) => {
    const result = await showConfirmation(...confirm);
    if (!result.isConfirmed) return;

    setBusyAction(`${user.id}:${Object.keys(changes)[0]}`);
    try {
      const response = await updateUserAPI(user.id, changes);
      if (!response) return;
      if (response.success) {
        replaceUser(response.data);
        showSuccess(response.message, "User updated");
        refreshUsers();
      } else {
        showError(response.message || "Couldn't update this user.");
      }
    } finally {
      setBusyAction(null);
    }
  };

  const toggleBlock = (user) =>
    isBlocked(user)
      ? changeUser(user, { status: "active" }, [
          `${user.name} will be able to sign in again.`,
          "Unblock this user?",
          "Unblock",
        ])
      : changeUser(user, { status: "blocked" }, [
          `${user.name} will be signed out everywhere and can't sign in until unblocked. Their results and subscriptions are kept.`,
          "Block this user?",
          "Block",
          "Cancel",
          "danger",
        ]);

  const toggleAdmin = (user) =>
    user.role === "admin"
      ? changeUser(user, { role: "user" }, [
          `${user.name} will lose access to this admin panel.`,
          "Remove admin access?",
          "Make student",
          "Cancel",
          "warning",
        ])
      : changeUser(user, { role: "admin" }, [
          `${user.name} will get full access to this admin panel, including users and payments.`,
          "Make this user an admin?",
          "Make admin",
          "Cancel",
          "warning",
        ]);

  const resetPassword = async (user) => {
    const result = await showConfirmation(
      `A new temporary password will be created for ${user.name}. Their current password stops working.`,
      "Reset password?",
      "Reset password",
      "Cancel",
      "warning"
    );
    if (!result.isConfirmed) return;

    setBusyAction(`${user.id}:password`);
    try {
      const response = await resetUserPasswordAPI(user.id);
      if (!response) return;
      if (response.success && response.temporary_password) {
        showInfo(
          `Temporary password for ${user.email}: ${response.temporary_password} — share it privately. They can change it after signing in.`,
          "Password reset"
        );
      } else {
        showError(response.message || "Couldn't reset the password.");
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const chip = ROLE_FILTERS.find((f) => f.key === roleFilter) || ROLE_FILTERS[0];
      await exportUsersCsv({ search: searchTerm.trim(), ...chip.params });
    } catch (err) {
      showError(err.message || "Couldn't export users.");
    } finally {
      setExporting(false);
    }
  };

  const perPageValue = pagination.per_page || perPage;
  const from = pagination.total ? (currentPage - 1) * perPageValue + 1 : 0;
  const to = Math.min(currentPage * perPageValue, pagination.total || 0);

  const openSubscriptions = (user) => {
    setDetailsUser(null);
    setExpandedSub(null);
    setSubscriptionsUser(user);
  };

  const renderRoleBadge = (user) =>
    user.role === "admin" ? (
      <span className="badge badge-brand">
        <i className="bi bi-shield-check"></i> Admin
      </span>
    ) : (
      <span className="badge badge-neutral">
        <i className="bi bi-person"></i> Student
      </span>
    );

  const renderBlockedBadge = (user) =>
    isBlocked(user) ? (
      <span className="badge badge-danger">
        <i className="bi bi-slash-circle"></i> Blocked
      </span>
    ) : null;

  const renderPlanBadge = (user) =>
    isPremiumUser(user) ? (
      <span className="badge badge-gold">
        <i className="bi bi-gem"></i> Premium
      </span>
    ) : (
      <span className="badge badge-info">Free</span>
    );

  const renderActions = (user) => (
    <div className="row-actions">
      <button type="button" className="btn-secondary btn-sm" onClick={() => setDetailsUser(user)}>
        <i className="bi bi-eye"></i> View
      </button>
      {(user.subscriptions || []).length > 0 && (
        <button
          type="button"
          className="btn-soft btn-icon btn-sm"
          onClick={() => openSubscriptions(user)}
          title="Subscriptions"
          aria-label="Subscriptions"
        >
          <i className="bi bi-credit-card-2-front"></i>
        </button>
      )}
    </div>
  );

  const detail = (label, value, span) =>
    value || value === 0 ? (
      <div className={`detail-cell ${span ? "span-2" : ""}`}>
        <span className="detail-label">{label}</span>
        <span className="detail-value">{value}</span>
      </div>
    ) : null;

  return (
    <div className="users-page page">
      <div className="page-header">
        <div>
          <h2>Users</h2>
          <p>Registered students and admins, with their subscriptions and activity.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-secondary" onClick={refreshUsers} disabled={loading}>
            <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`}></i>
            Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={handleExport} disabled={exporting}>
            {exporting ? <span className="btn-spinner"></span> : <i className="bi bi-download"></i>} Export CSV
          </button>
        </div>
      </div>

      {grantUser && (
        <GrantSubscriptionModal user={grantUser} onClose={() => setGrantUser(null)} onGranted={refreshUsers} />
      )}

      <ErrorAlert message={error} onRetry={refreshUsers} onDismiss={() => setError(null)} />

      <div className="stats-grid">
        <StatCard
          icon="bi bi-people"
          tone="brand"
          value={Number(totals?.all ?? pagination.total ?? 0).toLocaleString("en-IN")}
          label="Total users"
          hint={searchTerm.trim() ? "Matching the search" : undefined}
        />
        <StatCard
          icon="bi bi-gem"
          tone="warning"
          value={Number(totals?.subscribed ?? 0).toLocaleString("en-IN")}
          label="Premium"
          hint="With an active plan"
        />
        <StatCard
          icon="bi bi-person"
          tone="info"
          value={Number(totals?.students ?? 0).toLocaleString("en-IN")}
          label="Students"
        />
        <StatCard
          icon="bi bi-slash-circle"
          tone="danger"
          value={Number(totals?.blocked ?? 0).toLocaleString("en-IN")}
          label="Blocked"
        />
      </div>

      <div className="toolbar">
        <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Search by name, email or phone…" />
        <div className="chip-group">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip ${roleFilter === f.key ? "active" : ""}`}
              onClick={() => setRoleFilter(f.key)}
            >
              {f.label}
              {f.total && totals && <span className="chip-count">{Number(totals[f.total] ?? 0)}</span>}
            </button>
          ))}
        </div>
        <label className="users-per-page">
          <span>Rows</span>
          <select
            className="select-sm"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {[5, 10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="table-card">
        {loading ? (
          <LoadingState message="Loading users…" />
        ) : filteredUsers.length === 0 ? (
          <div className="users-empty">
            <EmptyState
              icon={roleFilter === "all" && !searchTerm ? "bi bi-people" : "bi bi-funnel"}
              title={roleFilter === "all" && !searchTerm ? "No users yet" : "No users match"}
              message={
                roleFilter === "all" && !searchTerm
                  ? "Registered users will appear here."
                  : "Try a different search or filter."
              }
              action={
                (searchTerm || roleFilter !== "all") && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setRoleFilter("all");
                      setSearchTerm("");
                    }}
                  >
                    Clear filters
                  </button>
                )
              }
            />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="table-scroll users-table-view">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Contact</th>
                    <th>Course</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Plan</th>
                    <th>Tests</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td className="text-muted">{(currentPage - 1) * perPageValue + index + 1}</td>
                      <td>
                        <div className="cell-user">
                          <span className="avatar">{initialsOf(user.name)}</span>
                          <div className="cell-stack">
                            <strong>
                              {user.name}
                              {user.email_verified_at && (
                                <i className="bi bi-patch-check-fill users-verified" title="Email verified"></i>
                              )}
                            </strong>
                            <small>{user.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cell-stack">
                          <strong>{user.contact_number || "—"}</strong>
                          <small>Joined {formatDate(user.created_at)}</small>
                        </div>
                      </td>
                      <td>
                        {user.course ? (
                          <span className="badge badge-neutral">{user.course}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {user.city || user.state ? (
                          <div className="cell-stack">
                            <strong>{user.city || "—"}</strong>
                            <small>{user.state}</small>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="cell-stack users-plan-cell">
                          {renderRoleBadge(user)}
                          {renderBlockedBadge(user)}
                        </div>
                      </td>
                      <td>
                        <div className="cell-stack users-plan-cell">
                          {renderPlanBadge(user)}
                          {activeSubsOf(user).length > 0 && <small>{activeSubsOf(user).length} active</small>}
                        </div>
                      </td>
                      <td>
                        <span className="users-attempts">{user.test_attempts_count || 0}</span>
                      </td>
                      <td>{renderActions(user)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="users-card-list">
              {filteredUsers.map((user) => (
                <div key={user.id} className="users-card">
                  <div className="cell-user">
                    <span className="avatar">{initialsOf(user.name)}</span>
                    <div className="cell-stack">
                      <strong>{user.name}</strong>
                      <small>{user.email}</small>
                    </div>
                  </div>
                  <div className="users-card-badges">
                    {renderRoleBadge(user)}
                    {renderBlockedBadge(user)}
                    {renderPlanBadge(user)}
                    {user.course && <span className="badge badge-neutral">{user.course}</span>}
                  </div>
                  <div className="users-card-meta">
                    <span>
                      <i className="bi bi-telephone"></i> {user.contact_number || "—"}
                    </span>
                    <span>
                      <i className="bi bi-pencil-square"></i> {user.test_attempts_count || 0} tests
                    </span>
                  </div>
                  {renderActions(user)}
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && pagination.total > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
            onPageChange={handlePageChange}
            info={`Showing ${from}–${to} of ${Number(pagination.total).toLocaleString("en-IN")} users`}
          />
        )}
      </div>

      {/* User details */}
      <Modal
        open={Boolean(detailsUser)}
        onClose={() => setDetailsUser(null)}
        size="lg"
        title={detailsUser?.name}
        subtitle={detailsUser?.email}
        footer={
          detailsUser && (
            <div className="users-actions">
              {String(detailsUser.id) !== String(me?.id) && (
                <>
                  <button
                    type="button"
                    className={isBlocked(detailsUser) ? "btn-secondary" : "btn-danger"}
                    onClick={() => toggleBlock(detailsUser)}
                    disabled={Boolean(busyAction)}
                  >
                    <i className={`bi ${isBlocked(detailsUser) ? "bi-unlock" : "bi-slash-circle"}`}></i>
                    {isBlocked(detailsUser) ? "Unblock" : "Block"}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => toggleAdmin(detailsUser)}
                    disabled={Boolean(busyAction)}
                  >
                    <i className={`bi ${detailsUser.role === "admin" ? "bi-person" : "bi-shield-check"}`}></i>
                    {detailsUser.role === "admin" ? "Make student" : "Make admin"}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => resetPassword(detailsUser)}
                disabled={Boolean(busyAction)}
              >
                <i className="bi bi-key"></i> Reset password
              </button>
              {detailsUser.role !== "admin" && (
                <button type="button" className="btn-soft" onClick={() => setGrantUser(detailsUser)}>
                  <i className="bi bi-gift"></i> Grant plan
                </button>
              )}
              {(detailsUser.subscriptions || []).length > 0 && (
                <button type="button" className="btn-soft" onClick={() => openSubscriptions(detailsUser)}>
                  <i className="bi bi-credit-card-2-front"></i> Subscriptions
                </button>
              )}
              <button type="button" className="btn-secondary users-actions-close" onClick={() => setDetailsUser(null)}>
                Close
              </button>
            </div>
          )
        }
      >
        {detailsUser && (
          <>
            <div className="users-profile-head">
              <span className="avatar avatar-lg">{initialsOf(detailsUser.name)}</span>
              <div className="users-card-badges">
                {renderRoleBadge(detailsUser)}
                {renderPlanBadge(detailsUser)}
                {detailsUser.email_verified_at ? (
                  <span className="badge badge-success">
                    <i className="bi bi-patch-check"></i> Email verified
                  </span>
                ) : (
                  <span className="badge badge-warning">Email not verified</span>
                )}
                <span className={`badge ${isBlocked(detailsUser) ? "badge-danger" : "badge-success"}`}>
                  {isBlocked(detailsUser) ? "Blocked" : "Active"}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-person"></i> Personal
              </div>
              <div className="detail-grid">
                {detail("Full name", detailsUser.name)}
                {detail("Phone", detailsUser.contact_number || "—")}
                {detail("Email", detailsUser.email, true)}
                {detail("Date of birth", detailsUser.date_of_birth)}
                {detail("Gender", detailsUser.gender)}
              </div>
            </div>

            {(detailsUser.city || detailsUser.state || detailsUser.country || detailsUser.pincode || detailsUser.address) && (
              <div className="detail-section">
                <div className="detail-section-title">
                  <i className="bi bi-geo-alt"></i> Location
                </div>
                <div className="detail-grid">
                  {detail("City", detailsUser.city)}
                  {detail("State", detailsUser.state)}
                  {detail("Country", detailsUser.country)}
                  {detail("PIN code", detailsUser.pincode)}
                  {detail("Address", detailsUser.address, true)}
                </div>
              </div>
            )}

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-mortarboard"></i> Academic & activity
              </div>
              <div className="detail-grid">
                {detail("Course", detailsUser.course || "Not selected")}
                {detail("Tests attempted", detailsUser.test_attempts_count || 0)}
                {detail("Education", detailsUser.education)}
                {detail("Institution", detailsUser.institution)}
                {detail("Year of study", detailsUser.year_of_study)}
                {detail(
                  "Average score",
                  detailsUser.average_score != null ? `${Number(detailsUser.average_score).toFixed(1)}%` : null
                )}
                {detail("Best score", detailsUser.best_score != null ? `${Number(detailsUser.best_score).toFixed(1)}%` : null)}
                {detail("Last test", detailsUser.last_test_date ? formatDate(detailsUser.last_test_date) : null)}
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-credit-card-2-front"></i> Subscriptions
              </div>
              <div className="detail-grid">
                {detail("Active", activeSubsOf(detailsUser).length)}
                {detail("Total", (detailsUser.subscriptions || []).length)}
                {(detailsUser.subscriptions || [])[0] &&
                  detail(
                    "Most recent",
                    `${detailsUser.subscriptions[0].course_plan?.plan_name || "Plan"} · ${
                      subStatus(detailsUser.subscriptions[0])
                    }${
                      detailsUser.subscriptions[0].expires_at
                        ? ` · expires ${formatDate(detailsUser.subscriptions[0].expires_at)}`
                        : ""
                    }`,
                    true
                  )}
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-clock-history"></i> Account
              </div>
              <div className="detail-grid">
                {detail("Created", formatDate(detailsUser.created_at))}
                {detail("Last updated", formatDate(detailsUser.updated_at))}
                {detail(
                  "Email verified",
                  detailsUser.email_verified_at ? formatDate(detailsUser.email_verified_at) : "Not verified"
                )}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Subscriptions */}
      <Modal
        open={Boolean(subscriptionsUser)}
        onClose={() => setSubscriptionsUser(null)}
        size="lg"
        icon="bi bi-credit-card-2-front"
        title="Subscriptions"
        subtitle={subscriptionsUser ? `${subscriptionsUser.name} · ${subscriptionsUser.email}` : undefined}
        footer={
          <button type="button" className="btn-secondary" onClick={() => setSubscriptionsUser(null)}>
            Close
          </button>
        }
      >
        {subscriptionsUser &&
          ((subscriptionsUser.subscriptions || []).length === 0 ? (
            <EmptyState icon="bi bi-credit-card" title="No subscriptions" message="This user hasn't subscribed yet." />
          ) : (
            <div className="users-sub-list">
              {subscriptionsUser.subscriptions.map((sub) => {
                const open = expandedSub === sub.id;
                return (
                  <div key={sub.id} className={`users-sub ${open ? "open" : ""}`}>
                    <button
                      type="button"
                      className="users-sub-head"
                      onClick={() => setExpandedSub(open ? null : sub.id)}
                      aria-expanded={open}
                    >
                      <div className="cell-stack">
                        <strong>{sub.course_plan?.plan_name || "Subscription plan"}</strong>
                        <small>
                          {formatDate(sub.starts_at)} – {formatDate(sub.expires_at)}
                          {sub.validity_days ? ` · ${sub.validity_days} days` : ""}
                        </small>
                      </div>
                      <div className="users-sub-right">
                        <strong>₹{Number(sub.amount || 0).toLocaleString("en-IN")}</strong>
                        <span className={`badge badge-dot ${statusTone(subStatus(sub))}`}>
                          {STATUS_LABELS[subStatus(sub)] || subStatus(sub)}
                        </span>
                        <i className="bi bi-chevron-down users-sub-caret"></i>
                      </div>
                    </button>
                    {open && (
                      <div className="users-sub-body">
                        <div className="detail-grid">
                          {detail("Description", sub.course_plan?.plan_description, true)}
                          {detail("Amount paid", `₹${Number(sub.amount || 0).toLocaleString("en-IN")}`)}
                          {detail(
                            "Plan price",
                            sub.course_plan?.plan_amount ? `₹${sub.course_plan.plan_amount}` : null
                          )}
                          {detail(
                            "Discount",
                            Number(sub.course_plan?.plan_discount) > 0 ? `₹${sub.course_plan.plan_discount}` : null
                          )}
                          {detail(
                            "Plan duration",
                            sub.course_plan?.plan_duration ? `${sub.course_plan.plan_duration} months` : null
                          )}
                          {detail("Purchased", formatDate(sub.created_at))}
                          {detail("Renewal", sub.is_renewal !== undefined ? (sub.is_renewal ? "Yes" : "No") : null)}
                          {detail(
                            "Payment ID",
                            sub.razorpay_payment_id ? <span className="mono">{sub.razorpay_payment_id}</span> : null,
                            true
                          )}
                          {detail(
                            "Order ID",
                            sub.razorpay_order_id ? <span className="mono">{sub.razorpay_order_id}</span> : null,
                            true
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
      </Modal>
    </div>
  );
}

export default Users;
